import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../utils/supabaseClient';
import styles from './ForgotPassword.module.css';
import { 
  FiMail, FiArrowRight, FiAlertCircle, FiCheckCircle, 
  FiSend, FiHome
} from 'react-icons/fi';
import bvLogo from '../../assets/bv.jpg';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) throw resetError;

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.forgotPage}>
      <div className={styles.container}>
        {/* Left Side - Branding */}
        <div className={styles.brandSection}>
          <div className={styles.brandContent}>
            <motion.div 
              className={styles.logoWrapper}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={bvLogo} alt="BrandVerse Logo" className={styles.brandLogo} />
            </motion.div>
            
            <motion.h1 
              className={styles.brandTitle}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Reset Password
            </motion.h1>
            
            <motion.p 
              className={styles.brandSubtitle}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Don't worry! We'll help you recover your account
            </motion.p>
            
            <motion.div 
              className={styles.helpText}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <p>We'll send a password reset link to your email address</p>
            </motion.div>
          </div>
          
          <div className={styles.brandFooter}>
            <Link to="/login" className={styles.backLink}>
              <FiArrowRight />
              Back to Login
            </Link>
          </div>
        </div>

        {/* Right Side - Reset Form */}
        <div className={styles.formSection}>
          <motion.div 
            className={styles.formContainer}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.formHeader}>
              <div className={styles.headerIcon}>
                <FiSend />
              </div>
              <h2>Forgot Password?</h2>
              <p>Enter your email address and we'll send you a link to reset your password</p>
            </div>

            {error && (
              <motion.div 
                className={styles.errorMessage}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <FiAlertCircle />
                <span>{error}</span>
              </motion.div>
            )}

            {success && (
              <motion.div 
                className={styles.successMessage}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <FiCheckCircle />
                <div className={styles.successContent}>
                  <strong>Reset link sent!</strong>
                  <p>Check your email for instructions to reset your password.</p>
                </div>
              </motion.div>
            )}

            {!success ? (
              <form onSubmit={handleResetPassword} className={styles.resetForm}>
                <div className={styles.inputGroup}>
                  <label htmlFor="email">Email Address</label>
                  <div className={styles.inputWrapper}>
                    <FiMail className={styles.inputIcon} />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className={styles.resetButton}
                  disabled={loading}
                >
                  {loading ? (
                    <div className={styles.spinner}></div>
                  ) : (
                    <>
                      Send Reset Link <FiSend />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className={styles.afterReset}>
                <button 
                  onClick={() => navigate('/login')} 
                  className={styles.backToLogin}
                >
                  <FiHome />
                  Back to Login
                </button>
                <button 
                  onClick={() => window.location.reload()} 
                  className={styles.resendButton}
                >
                  Didn't receive email? Click here to resend
                </button>
              </div>
            )}

            <div className={styles.helpInfo}>
              <p>
                Remember your password?{' '}
                <Link to="/login" className={styles.loginLink}>
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;